import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import * as services from "../../../store/service";
import * as CSIDActions from "../../../store/csid/actions";
import { ICSIDUserStatus } from "../../../store/csid/types";
import IDFCreditAuthentication from "../../Credit/Auth/IDFCreditAuthentication";
import { DialogComponent } from "../../common/DialogComponent";
import { ezsServiceIds, idfServiceIds } from "../../../config/UserServicesList";
import { useAreServicesActive } from "../../../util/CommonServices";

interface IProps {
    callback?: any;
}
const TUAuthentication: React.FunctionComponent<IProps> = (props: any) => {
    const dispatch = useDispatch();
    const [showAuthenticationDialog, setShowAuthenticationDialog] = useState(false);
    const refreshUserCSIDStatus = () => dispatch(CSIDActions.actionCreators.getUserCSIDStatus(true));
    const refreshContext = () => dispatch(CSIDActions.actionCreators.componentNeedsRefresh());
    const csidUserStatus = useSelector((state: ApplicationState) =>
        state.csidUserStatusState && state.csidUserStatusState.csidUserStatus ? state.csidUserStatusState.csidUserStatus : null
    );
    const isLoadingCSIDUserStatus = useSelector((state: ApplicationState) => state?.csidUserStatusState?.isLoading);

    const isPrimaryAccount = useSelector((state: ApplicationState) =>
        state.loggedInUser && state.loggedInUser.activeAccount && state.loggedInUser.activeAccount.accountType === "PRIMARY" ? true : false
    );
    const partnerId: number = useSelector((state: ApplicationState) =>
        state.configurationState && state.configurationState.configuration.partnerConfig.partnerId
            ? state.configurationState.configuration.partnerConfig.partnerId
            : 0
    );
    const userCountry = useSelector((state: ApplicationState) =>
        state.myAccountServiceState?._unifiedAccountInfo?.country
    );
    const countryRequiresAuthentication = useSelector((state: ApplicationState) => {
        return ['US', 'CA'].includes(userCountry || '');
    });

    const hasPurchaseCreditReport = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.hasPurchaseCreditReport);
    const hasOneTimeScore = useSelector((state: ApplicationState) => state.creditHistoryData && state.creditHistoryData.hasOneTimeScore);
    const creditScoreServiceList = [
        ezsServiceIds.quarterlyCreditScore,
        ezsServiceIds.tuDailyCreditScore,
        ezsServiceIds.tuMonthlyCreditScore,
        ezsServiceIds.tuQuarterlyCreditScore,
        ezsServiceIds.tuAnnualCreditScore,
        ezsServiceIds.tuCanadaCreditScore,
        ezsServiceIds.monthlyCreditScore,
        idfServiceIds.threeBureauCreditScores,
        idfServiceIds.transUnionCreditScore,
        ezsServiceIds.tuCreditReportWithScore,
        ezsServiceIds.tuOneTimeCreditReportWithScore,
        ezsServiceIds.tuDailyCreditReportWithScore,
        ezsServiceIds.tuMonthlyCreditReportWithScore,
        ezsServiceIds.tuQuarterlyCreditReportWithScore,
        ezsServiceIds.tuAnnualCreditReportWithScore,
        ezsServiceIds.experianAnnualCreditReportWithScore,
        ezsServiceIds.tuCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuMonthlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuQuarterlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuCanadaMonthlyCreditReportWithScore,
        ezsServiceIds.tuCanadaQuarterlyCreditReportWithScore,
        ezsServiceIds.tuCanadaAnnualCreditReportWithScore,
        ezsServiceIds.tuCanadaOneTimeCreditReportWithScore,
    ];
    const isCreditScoreActive = useAreServicesActive(creditScoreServiceList) || (hasPurchaseCreditReport && hasOneTimeScore);

    const creditAuthCallback = (callbackObj: any) => {
        refreshUserCSIDStatus();
        refreshContext();
        closeAuthenticationDialog();
        if (props.callback) {
            props.callback();
        }
    };

    const closeAuthenticationDialog = () => {
        setShowAuthenticationDialog(false);
    };

    useEffect(() => {
        const authStatus = csidUserStatus?.["auth-status"]?.toLocaleLowerCase();
        const isAuthPending = authStatus === "not authenticated" || authStatus === "in progress";
        const isEligibleUser = csidUserStatus?.["isIDFUser"] || (csidUserStatus?.["isTUUser"] && csidUserStatus?.["message"] !== "tu-down");
        const authenticationRequired = countryRequiresAuthentication && isAuthPending && isEligibleUser;

        if (authenticationRequired && isPrimaryAccount) {
            services.isTuCanadianUser().then((response) => {

                const apiSaysCanadian = response && !response.hasError && response.isCanadiaUser === true;
                // Source of truth
                const isCanadian = userCountry === "CA";

                // keep API as fallback if country is ever undefined
                const isEffectivelyCanadian = isCanadian ?? apiSaysCanadian;

                let shouldShowDialog = false;

                if (isEffectivelyCanadian) {
                    // CA Require credit service
                    shouldShowDialog = !!isCreditScoreActive;
                } else {
                    // US Default behavior
                    shouldShowDialog = true;
                }

                if (shouldShowDialog) {
                    setShowAuthenticationDialog(true);
                }
            });
        }
    }, [csidUserStatus, countryRequiresAuthentication, isPrimaryAccount, userCountry, isCreditScoreActive]);

    return (
        <React.Fragment>
            {isLoadingCSIDUserStatus && <div style={{ display: "block" }} className="dialog-overlay"></div>}

            {showAuthenticationDialog && (
                <DialogComponent
                    dialogId="dialog-tu-l3-authentication"
                    title="TU L3 Authentication"
                    closeDialog={closeAuthenticationDialog}
                    hideCloseButton={true}
                    noEsc={true}
                >
                    <IDFCreditAuthentication
                        initCSIDUserStatus={csidUserStatus as ICSIDUserStatus}
                        creditAuthCallback={creditAuthCallback}
                        displayFlowAsDialog={false}
                    />
                </DialogComponent>
            )}
        </React.Fragment>
    );
};
export default TUAuthentication;
